import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { Folder } from '@utils/types/folders/folder.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { URL_EXTENSION } from '@utils/constants/app-constants.ts';

export interface FolderFilters {
  offset?: number;
  limit?: number;
  q?: string | string[]; // Support multiple q parameters
  sort?: string;
  direction?: 'asc' | 'desc';
  type?: 'folders' | 'documents';
  sortType?: 'folders-first' | 'files-first';
  from?: string; // ISO 8601 date
  to?: string; // ISO 8601 date
}

export const fetchFolder = async (
  folderId: string,
  filters: FolderFilters = {}
): Promise<BaseResponse<Folder>> => {
  const {
    offset = 0,
    limit = 10,
    q,
    sort,
    direction = 'asc',
    type,
    sortType,
    from,
    to
  } = filters;

  // Build URL with URLSearchParams to handle multiple q parameters
  const params = new URLSearchParams();
  params.set('offset', String(offset));
  params.set('limit', String(limit));

  // Backend accepts: last_modified | created | name | type. The workspace
  // table exposes an "Uploaded" column keyed on `date_upload` (a formatted
  // display field) — map it to `created` (the DB timestamp the backend can
  // actually sort by). Same for any other FE-only alias we introduce here.
  // Fixes #2035: "I can not order the table on 'UPLOADED'".
  const SORT_FIELD_ALIASES: Record<string, string> = {
    date_upload: 'created',
    uploaded: 'created'
  };
  // sort=type passes through as-is now that the backend union projects
  // 'caption' | 'transcript' | 'folder' (not just 'document') — a real 3-way
  // sort at the DB level (#2035). FE additionally re-sorts within each page
  // to handle the response's split folders[]/documents[] arrays.
  const backendSort = sort ? (SORT_FIELD_ALIASES[sort] ?? sort) : undefined;
  if (backendSort) params.set('sort', backendSort);
  if (direction) params.set('direction', direction);
  if (type) params.set('type', type);
  if (sortType) params.set('sort_type', sortType);
  if (from) params.set('from', from);
  if (to) params.set('to', to);

  // Handle multiple q parameters
  if (q) {
    const qArray = Array.isArray(q) ? q : [q];
    qArray.forEach(qValue => {
      if (qValue) params.append('q', qValue);
    });
  }

  const url = `${URL_EXTENSION}/folders/${folderId}/?${params.toString()}`;
  const { data } = await Axios.get(url);
  return data;
};

export const useGetFolder = (
  folderId: string | undefined,
  filters: FolderFilters = {},
  options?: UseQueryOptions<
    BaseResponse<Folder>,
    AxiosError<BaseError>,
    BaseResponse<Folder>
  >
) => {
  const queryKey = [
    'folder',
    folderId,
    filters.offset,
    filters.limit,
    filters.q,
    filters.sort,
    filters.direction,
    filters.type,
    filters.sortType,
    filters.from,
    filters.to
  ];

  return useQuery<BaseResponse<Folder>, AxiosError<BaseError>>(
    queryKey,
    () => fetchFolder(folderId!, filters),
    {
      enabled: !!folderId,
      refetchInterval: 10000,
      refetchIntervalInBackground: false, // Stop polling when tab is in background
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        // Don't retry on 404 or 422 (folder doesn't exist)
        if (
          error?.response?.status === 404 ||
          error?.response?.status === 422
        ) {
          return false;
        }
        // Default retry logic (3 times)
        return failureCount < 3;
      },
      ...options
    }
  );
};
